import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import OrtizHomeruns from '../graphics/OrtizHomeruns';
import HomerunsByYear from '../graphics/HomerunsByYear';
import OrtizLegend from '../graphics/OrtizLegend';
import OrtizSelects from '../graphics/OrtizSelects';
import Scrollytelling from '../graphics/Scrollytelling';
import filters from '../../assets/data/filters.json';

import '~styles/modules/graphic.scss';

const Graphic = ({ data }) => {
  // setting state for the chart width and home run filters
  const [chartWidth, setChartWidth] = useState();
  const [numberFilter, setNumberFilter] = useState([]);
  const [opponentFilter, setOpponentFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [seasonTypeFilter, setSeasonTypeFilter] = useState('all');
  const [seriesTypeFilter, setSeriesTypeFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [walkoffFilter, setWalkoffFilter] = useState('all');

  const [filtersDisabled, setFiltersDisable] = useState(true);

  const [filterDisplay, setFilterDisplay] = useState(false);

  // ref for the graphics container
  const graphicsContainerRef = useRef(null);

  // sort the teams and stadium names alphabetically
  filters.teams.sort((a, b) => (a.full < b.full ? -1 : 1));
  filters.stadium.sort();

  // gets the select element changed and it's value
  // and updates the state of the homerunFilters
  const updateSelectors = useCallback((e) => {
    const selector = e.target.getAttribute('data-id');
    switch (selector) {
      case 'opponent': {
        setOpponentFilter(e.target.value);
        break;
      }
      case 'year': {
        setYearFilter(e.target.value);
        break;
      }
      case 'seasonType': {
        setSeasonTypeFilter(e.target.value);
        break;
      }
      case 'seriesType': {
        setSeriesTypeFilter(e.target.value);
        break;
      }
      case 'venue': {
        setVenueFilter(e.target.value);
        break;
      }
      case 'result': {
        setResultFilter(e.target.value);
        break;
      }
      case 'walkoff': {
        setWalkoffFilter(e.target.value);
        break;
      }
      default: {
        return null;
      }
    }
  }, []);

  // resets the homerunFilters back to original settings
  const clearFilters = useCallback(() => {
    setNumberFilter([]);
    setOpponentFilter('all');
    setYearFilter('all');
    setSeasonTypeFilter('all');
    setSeriesTypeFilter('all');
    setVenueFilter('all');
    setResultFilter('all');
    setWalkoffFilter('all');
  }, []);

  useEffect(() => {
    // updates the chart width using the width of the graphic's container element
    function resizeGraphic() {
      setChartWidth(graphicsContainerRef.current.offsetWidth);
    }

    // sets the chart Width once the graphic's container is mounted
    // and adds an event listener for a window resize to update the chart width
    if (graphicsContainerRef.current) {
      setChartWidth(graphicsContainerRef.current.offsetWidth);
      window.addEventListener('resize', resizeGraphic);
    }

    // cleans up the event listener
    return _ => {
      window.removeEventListener('resize', resizeGraphic);
    };
  }, [graphicsContainerRef]);

  return (
    <div className={`graphic__custom graphic--${data.size}`} id={data.graphic}>
      <h5 className="graphic__hed benton-n6">{data.hed}</h5>
      <p className="graphic__chatter benton-n4">
        {data.graphic === 'ortiz-homeruns'
          ? 'We chose 12 home runs that tell the story of Ortiz’s career. Begin scrolling to read more about them. Then, explore all 558 of Ortiz’s regular and postseason homers.'
          : data.chatter
        }
      </p>
      {/*
        Custom graphic code goes here, either inline or as an imported component.
        If multiple custome graphics per page, use a switch or if statements to target
        the correct component. Graphic specific legends would also go here or in the
        imported component file.
      */}
      <div className="graphic__shell">
        {data.graphic === 'homeruns-by-year' && <HomerunsByYear />}
        {data.graphic === 'ortiz-homeruns' && <OrtizLegend />}
        {data.graphic === 'ortiz-homeruns' && resultFilter
          && (
            <OrtizSelects
              opponentFilter={opponentFilter}
              yearFilter={yearFilter}
              seasonTypeFilter={seasonTypeFilter}
              seriesTypeFilter={seriesTypeFilter}
              venueFilter={venueFilter}
              resultFilter={resultFilter}
              walkoffFilter={walkoffFilter}
              updateFilters={updateSelectors}
              clearFilters={clearFilters}
              filtersDisabled={filtersDisabled}
              filterDisplay={filterDisplay}
              setFilterDisplay={setFilterDisplay}
            />
          )}
        <div className="graphic__container" ref={graphicsContainerRef}>
          {data.graphic === 'ortiz-homeruns'
            && chartWidth
            && resultFilter
            && venueFilter
            && (
              <OrtizHomeruns
                width={chartWidth}
                opponentFilter={opponentFilter}
                yearFilter={yearFilter}
                seasonTypeFilter={seasonTypeFilter}
                seriesTypeFilter={seriesTypeFilter}
                venueFilter={venueFilter}
                resultFilter={resultFilter}
                walkoffFilter={walkoffFilter}
                numberFilter={numberFilter}
                filtersDisabled={filtersDisabled}
              />
            )}
        </div>
        <div className="graphic__footer">
          <p className="graphic__source bentonsanscond-n4">Source: {data.source}. {data.notes ? `Notes: ${data.notes}` : null}</p>
          {data.credit ? <p className="graphic__credit bentonsanscond-n4">{data.credit}/Globe Staff</p> : null}
        </div>
      </div>
      {data.graphic === 'ortiz-homeruns'
        && (
          <Scrollytelling
            setNumberFilter={setNumberFilter}
            setVenueFilter={setVenueFilter}
            setFiltersDisable={setFiltersDisable}
            setFilterDisplay={setFilterDisplay}
            clearFilters={clearFilters}
          />
        )}
    </div>
  );
};

export default Graphic;

Graphic.propTypes = {
  data: PropTypes.shape({
    graphic: PropTypes.string,
    size: PropTypes.string,
    hed: PropTypes.string,
    chatter: PropTypes.string,
    source: PropTypes.string,
    notes: PropTypes.string,
    credit: PropTypes.string,
  }).isRequired,
};
